import { asRecord } from "@arti/utilities";

import type {
  StoryFlowLockDiagnostics,
  StoryFlowLockMetrics,
  StoryFlowLockRecord,
} from "../types/storyFlowLocks";

const EXPIRING_SOON_THRESHOLD_MS = 2 * 60 * 1000;
const STALE_THRESHOLD_MS = 5 * 60 * 1000;

type PlainObject = Record<string, unknown>;

export function parseLockDiagnostics(
  payload: unknown,
): StoryFlowLockDiagnostics {
  const diagnostics = toPlainObject(payload);
  const locksPayload = Array.isArray(diagnostics?.locks)
    ? diagnostics.locks
    : [];
  const locks = locksPayload
    .map((value) => parseLockRecord(value))
    .filter((value): value is StoryFlowLockRecord => Boolean(value));

  const metrics = parseMetrics(diagnostics?.metrics, locks);
  const generatedAt =
    typeof diagnostics?.generated_at === "number"
      ? diagnostics.generated_at
      : Date.now();

  return { generatedAt, locks, metrics };
}

function parseMetrics(
  value: unknown,
  locks: StoryFlowLockRecord[],
): StoryFlowLockMetrics {
  const metrics = toPlainObject(value);
  if (metrics) {
    const validated = validateMetrics(metrics);
    if (validated) {
      return validated;
    }
  }

  const now = Date.now();
  const expiringSoon = locks.filter(
    (lock) =>
      lock.lockExpiresAt !== null &&
      lock.lockExpiresAt - now <= EXPIRING_SOON_THRESHOLD_MS,
  ).length;
  const staleLocks = locks.filter(
    (lock) =>
      lock.updatedAt !== null && now - lock.updatedAt >= STALE_THRESHOLD_MS,
  ).length;
  const owners = new Set(locks.map((lock) => lock.lockOwner).filter(Boolean));

  return {
    activeLocks: locks.length,
    activeOwners: owners.size,
    expiringSoon,
    staleLocks,
    totalLocks: locks.length,
  };
}

function validateMetrics(metrics: PlainObject): StoryFlowLockMetrics | null {
  const requiredKeys: (keyof StoryFlowLockMetrics)[] = [
    "activeLocks",
    "expiringSoon",
    "activeOwners",
    "staleLocks",
    "totalLocks",
  ];
  if (requiredKeys.every((key) => typeof metrics[key] === "number")) {
    return {
      activeLocks: Number(metrics.activeLocks),
      activeOwners: Number(metrics.activeOwners),
      expiringSoon: Number(metrics.expiringSoon),
      staleLocks: Number(metrics.staleLocks),
      totalLocks: Number(metrics.totalLocks),
    };
  }
  return null;
}

function parseLockRecord(value: unknown): StoryFlowLockRecord | null {
  const record = toPlainObject(value);
  if (!record) {
    return null;
  }
  const storyIssueKey = optionalString(record.story_issue_key);
  if (!storyIssueKey) {
    return null;
  }

  return {
    activeFlowId: optionalString(record.active_flow_id),
    activeFlowStatus: optionalString(record.active_flow_status),
    activeFlowType: optionalString(record.active_flow_type),
    lockExpiresAt: numberOrNull(record.lock_expires_at),
    lockOwner: optionalString(record.lock_owner),
    storyIssueKey,
    updatedAt: numberOrNull(record.updated_at),
  };
}

function optionalString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function numberOrNull(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toPlainObject(value: unknown): PlainObject | null {
  return asRecord(value);
}
